import React, { useState, useEffect, useCallback } from 'react';
import { FiMapPin, FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw, FiStar, FiZap, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import CityService from '../services/CityService';

const Cities = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTop, setFilterTop] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Server-side Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [modal, setModal] = useState(null); // null | 'add' | { edit: city }
  const [form, setForm] = useState({
    name: '',
    code: '',
    stateName: 'India',
    stateCode: '',
    latitude: 0,
    longitude: 0,
    isTopCity: false,
    seq: 999,
  });

  const loadCities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CityService.getAllCities({
        page: currentPage,
        limit: pageSize,
        search: search.trim() || undefined,
        topOnly: filterTop ? true : undefined,
      });

      if (res && res.cities) {
        setCities(res.cities);
        setTotalCount(res.total || res.cities.length);
        setTotalPages(res.totalPages || 1);
      } else if (Array.isArray(res)) {
        setCities(res);
        setTotalCount(res.length);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Failed to load cities:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, search, filterTop]);

  useEffect(() => {
    loadCities();
  }, [loadCities]);

  // Reset to page 1 when search or filterTop or pageSize changes
  const handleSearchChange = (val) => {
    setSearch(val);
    setCurrentPage(1);
  };

  const handleFilterTopChange = () => {
    setFilterTop(prev => !prev);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const openAdd = () => {
    setForm({
      name: '',
      code: '',
      stateName: 'Punjab',
      stateCode: 'PB',
      latitude: 30.7333,
      longitude: 76.7794,
      isTopCity: false,
      seq: 999,
    });
    setModal('add');
  };

  const openEdit = (city) => {
    setForm({
      name: city.name || '',
      code: city.code || '',
      stateName: city.stateName || '',
      stateCode: city.stateCode || '',
      latitude: city.latitude || 0,
      longitude: city.longitude || 0,
      isTopCity: Boolean(city.isTopCity),
      seq: city.seq || 999,
    });
    setModal({ edit: city });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('City name is required'); return; }
    const code = form.code || form.name.toLowerCase().replace(/ /g, '-').replace(/[^\w-]/g, '');
    const slug = code;
    const payload = { ...form, code, slug };

    try {
      if (modal === 'add') {
        await CityService.addCity(payload);
      } else if (modal?.edit) {
        await CityService.updateCity(modal.edit._id, payload);
      }
      setModal(null);
      loadCities();
    } catch (err) {
      alert('Failed to save city: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleToggleTop = async (id) => {
    try {
      await CityService.toggleTopCity(id);
      loadCities();
    } catch (err) {
      alert('Failed to update top city status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this city permanently?')) return;
    try {
      await CityService.deleteCity(id);
      loadCities();
    } catch (err) {
      alert('Failed to delete city');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await CityService.syncCities();
      alert('Cities synced successfully!');
      loadCities();
    } catch (err) {
      alert('Sync failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const startIndex = (currentPage - 1) * pageSize;

  return (
    <div className="space-y-6 max-w-6xl text-left">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2 tracking-tight">
            <FiMapPin className="text-purple-700" /> Cities & Locations
          </h1>
          <p className="text-xs font-medium text-slate-500 mt-1">{totalCount} total cities configured</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSync} disabled={syncing} className="px-3.5 py-2 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-bold transition flex items-center gap-1.5 border border-slate-200 shadow-sm">
            <FiZap className={`w-3.5 h-3.5 text-amber-600 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing...' : 'Sync Cities'}
          </button>
          <button onClick={loadCities} className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition shadow-sm" title="Refresh">
            <FiRefreshCw className="w-4 h-4" />
          </button>
          <button onClick={openAdd} className="px-4 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition flex items-center gap-1.5 shadow-sm">
            <FiPlus className="w-4 h-4" /> Add City
          </button>
        </div>
      </div>

      {/* Search, Top Filter & Page Size Selector */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search city by name, state, or code..."
            className="w-full bg-white border border-slate-300 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 transition shadow-sm"
          />
        </div>
        <button
          onClick={handleFilterTopChange}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 shrink-0 ${filterTop ? 'bg-amber-50 text-amber-900 border border-amber-200' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'}`}
        >
          <FiStar className={`w-3.5 h-3.5 ${filterTop ? 'fill-current text-amber-600' : ''}`} /> Top Cities Only
        </button>
        <select
          value={pageSize}
          onChange={e => handlePageSizeChange(Number(e.target.value))}
          className="bg-white border border-slate-300 text-slate-700 text-xs font-bold rounded-xl px-3 py-2.5 outline-none focus:border-purple-600 shrink-0 shadow-sm"
        >
          <option value={15}>15 per page</option>
          <option value={30}>30 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-7 h-7 border-2 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : cities.length === 0 ? (
          <div className="text-center py-20">
            <FiMapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-500">No cities found</p>
          </div>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100/70">
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">City</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Code</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">State</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Coordinates</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Top Featured</th>
                    <th className="px-4 py-3 text-right text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {cities.map((c) => (
                    <tr key={c._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3.5">
                        <p className="text-xs font-extrabold text-slate-900">{c.name}</p>
                        <p className="text-[10px] text-slate-500 font-medium">{c.slug}</p>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-purple-700 font-mono font-bold">{c.code}</td>
                      <td className="px-4 py-3.5 text-xs font-semibold text-slate-700">{c.stateName || '-'}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-600 font-mono">
                        {c.latitude ? `${c.latitude.toFixed(2)}, ${c.longitude?.toFixed(2)}` : '-'}
                      </td>
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => handleToggleTop(c._id)}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-extrabold transition flex items-center gap-1 border ${c.isTopCity ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}
                        >
                          <FiStar className={`w-3 h-3 ${c.isTopCity ? 'fill-current text-emerald-600' : ''}`} /> {c.isTopCity ? 'Top City' : 'Standard'}
                        </button>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg text-slate-500 hover:text-purple-700 hover:bg-purple-50 transition" title="Edit">
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDelete(c._id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition" title="Delete">
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Backend Pagination Controls */}
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-slate-600 font-medium">
                Showing <span className="text-slate-900 font-bold">{startIndex + 1}</span> to <span className="text-slate-900 font-bold">{Math.min(startIndex + cities.length, totalCount)}</span> of <span className="text-slate-900 font-bold">{totalCount}</span> cities
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                  <FiChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-slate-700 px-3 font-bold">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-md space-y-4 text-left shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="text-sm font-bold text-slate-900">{modal === 'add' ? 'Add Custom City' : 'Edit City'}</h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>

            <div className="space-y-3">
              <Field label="City Name *" value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))} placeholder="e.g. Chandigarh" />
              <Field label="City Code" value={form.code} onChange={v => setForm(p => ({ ...p, code: v }))} placeholder="e.g. CHAND, DEL" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="State Name" value={form.stateName} onChange={v => setForm(p => ({ ...p, stateName: v }))} placeholder="Punjab" />
                <Field label="State Code" value={form.stateCode} onChange={v => setForm(p => ({ ...p, stateCode: v }))} placeholder="PB" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Latitude" value={form.latitude} onChange={v => setForm(p => ({ ...p, latitude: Number(v) }))} placeholder="30.7333" />
                <Field label="Longitude" value={form.longitude} onChange={v => setForm(p => ({ ...p, longitude: Number(v) }))} placeholder="76.7794" />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox" id="topCheck" checked={form.isTopCity} onChange={e => setForm(p => ({ ...p, isTopCity: e.target.checked }))}
                  className="rounded text-purple-600"
                />
                <label htmlFor="topCheck" className="text-xs text-slate-700 font-medium cursor-pointer">Feature as Top City on App Home</label>
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition">Save City</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">{label}</label>
    <input
      type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium transition"
    />
  </div>
);

export default Cities;
